/**
 * Hook for fetching system health data for admin dashboard
 */

import { useAuth } from '@contexts/AuthContext';
import type { SettlementMonitorReport } from '@parlay-picker/shared/settlementMonitor';
import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export interface UserActivityStats {
  totalUsers: number;
  activeUsers24h: number;
  activeUsers7d: number;
  newUsersToday: number;
  newUsersLast7d: number;
  newUsersLast30d: number;
  newUsersLast6m: number;
  newUsersLastYear: number;
}

export interface BettingDataStats {
  totalParlaysTracked: number;
  parlaysPendingSettlement: number;
  picksGeneratedToday: number;
}

export interface ApiHealthStatus {
  name: string;
  status: 'healthy' | 'degraded' | 'down' | 'unknown';
  lastSuccessfulFetch: string | null;
  responseTimeMs: number | null;
  error: string | null;
}

export interface SettlementStats {
  pendingLegs: number;
  pendingParlays: number;
  gamesAwaitingSettlement: number;
  lastSettlementRun: string | null;
}

export interface SystemStatus {
  overall: 'healthy' | 'degraded' | 'down';
  database: boolean;
  cacheHitRate: number;
  oddsApiQuota: number | null;
}

/** What the response cache saved: each replay avoided one run of its original generation. */
export interface ResponseCacheSavings {
  replays: number;
  tokensSaved: number;
  costSaved: number;
}

export interface LlmUsagePeriod {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  totalCost: number;
  /** Absent from a server deployed before 2026-10-07. */
  responseCache?: ResponseCacheSavings;
}

export interface LlmUsageByProvider {
  provider: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  totalCost: number;
}

export interface LlmUsageStats {
  today: LlmUsagePeriod;
  last7d: LlmUsagePeriod;
  last30d: LlmUsagePeriod;
  allTime: LlmUsagePeriod;
  byProvider: LlmUsageByProvider[];
}

export interface SystemHealthData {
  userActivity: UserActivityStats;
  bettingData: BettingDataStats;
  apiHealth: ApiHealthStatus[];
  settlement: SettlementStats;
  /** Absent from a server that predates it; null when its queries failed. */
  settlementMonitor?: SettlementMonitorReport | null;
  systemStatus: SystemStatus;
  llmUsage: LlmUsageStats;
  timestamp: string;
}

export interface UseSystemHealthResult {
  health: SystemHealthData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useSystemHealth(): UseSystemHealthResult {
  const { authFetch } = useAuth();
  const [health, setHealth] = useState<SystemHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(apiUrl('/api/admin/system-health'));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Unauthorized - admin access required');
        }
        throw new Error('Failed to fetch system health');
      }

      const data: SystemHealthData = await response.json();
      setHealth(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  return {
    health,
    loading,
    error,
    refetch: fetchHealth,
  };
}
